import styled, { css, keyframes } from "styled-components";

// Animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

// Layout Components
export const SectionHeader = styled.div`
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--accent-light);
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

export const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0;
`;

export const SectionActions = styled.div`
  display: flex;
  gap: 15px;
`;
export const Button = styled.button`
  padding: 12px 25px;
  border: none;
  border-radius: 20px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;

  ${(props) =>
    props.$small &&
    `
    padding: 8px 16px;
    font-size: 12px;
  `}

  ${(props) =>
    props.$primary &&
    `
    background: var(--gradient-primary);
    color: var(--surface);
    
    &:hover:not(:disabled) {
      background: var(--accent-dark);
      color: var(--surface);
      transform: translateY(-2px);
    }
  `}

  ${(props) =>
    props.$secondary &&
    `
    background: transparent;
    color: var(--text-secondary);
    border: 2px solid var(--accent-light);
    
    &:hover:not(:disabled) {
      background: var(--accent-light);
      border-color: var(--accent);
      color: var(--accent);
      transform: translateY(-2px);
    }
  `}

     svg {
    animation: ${(props) =>
      props.$loading
        ? css`
            ${spin} 1s ease-in-out infinite
          `
        : "none"};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// Dashboard Components
export const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const DashboardCard = styled.div`
  background: var(--surface);
  padding: 30px;
  border-radius: 20px;
  border: 2px solid var(--accent-light);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: ${slideIn} 0.6s ease-out;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${(props) => {
      switch (props.$type) {
        case "revenue":
          return "var(--success)";
        case "users":
          return "var(--info)";
        case "courses":
          return "var(--warning)";
        case "orders":
          return "var(--accent)";
        default:
          return "var(--accent)";
      }
    }};
  }

  &:hover {
    transform: translateY(-5px);
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

export const CardIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: var(--surface);
  background: ${(props) => {
    switch (props.$type) {
      case "revenue":
        return "var(--success)";
      case "users":
        return "var(--info)";
      case "courses":
        return "var(--warning)";
      case "orders":
        return "var(--accent)";
      default:
        return "var(--accent)";
    }
  }};
`;

export const CardValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 10px;
`;

export const CardLabel = styled.div`
  font-size: 14px;
  text-transform: uppercase;
  color: var(--text-secondary);
  /* margin-bottom: 15px; */
`;

export const CardChange = styled.div`
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
  color: ${(props) => (props.$positive ? "var(--success)" : "var(--error)")};
`;

// Chart Components
export const ChartContainer = styled.div`
  background: var(--surface);
  border-radius: 15px;
  padding: 30px;
  margin: 20px 0;
  border: 2px solid var(--accent-light);
  text-align: center;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 15px;
  animation: ${slideIn} 0.6s ease-out;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(124, 14, 51, 0.02);
  }
`;

export const ChartPlaceholder = styled.div`
  font-size: 48px;
  color: var(--accent);
`;

export const ChartTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
`;

export const ChartDescription = styled.p`
  color: var(--text-muted);
  font-size: 12px;
  margin-top: 10px;
`;

// Table Components
export const DataTableScroll = styled.div`
  width: 100% !important;
  overflow-x: auto !important;
  overflow-y: visible !important;
  border-radius: 15px;
  box-shadow: var(--shadow-soft);
  max-width: none !important;
  min-width: 0 !important;
  flex-shrink: 0 !important;
`;

export const DataTable = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr 1fr 1fr;
  min-width: 800px;
  background: var(--surface);
  border-radius: 15px;
  border: 2px solid var(--accent-light);
  overflow: hidden;
  animation: ${slideIn} 0.6s ease-out;
`;

export const TableHeader = styled.div`
  padding: 15px 20px;
  border-bottom: 2px solid var(--accent-light);
  background-color: var(--accent);
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  white-space: nowrap;
  letter-spacing: 1px;
  color: white;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: ${(props) => props.$align || "left"};
`;

export const TableRow = styled.div`
  display: contents;
`;

export const TableCell = styled.div`
  padding: 15px 20px;
  text-align: left;
  border-bottom: 1px solid var(--accent-light);
  font-size: 14px;
  color: var(--text-primary);
  display: flex;
  align-items: center;
`;

export const LoadingCell = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 20px;
`;

export const EmptyCell = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 20px;
  color: var(--text-muted);
`;

// User Components
export const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const UserAvatar = styled.div`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: var(--surface);
  font-weight: 600;
  text-transform: uppercase;
`;

export const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;

  ${(props) => {
    switch (props.$type) {
      case "active":
        return `
          background: var(--success);
          color: var(--surface);
        `;
      case "pending":
        return `
          background: var(--warning);
          color: var(--surface);
        `;
      case "inactive":
        return `
          background: var(--text-muted);
          color: var(--surface);
        `;
      case "completed":
        return `
          background: var(--info);
          color: var(--surface);
        `;
      default:
        return `
          background: var(--text-muted);
          color: var(--surface);
        `;
    }
  }}
`;

// Activities Section
export const ActivitiesSection = styled.div`
  margin-top: 40px;
`;

export const ActivitiesTitle = styled.h3`
  margin-bottom: 20px;
  text-transform: uppercase;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 10px;
`;
